import ExerciseSupport from '../../../student/components/exercises/ExerciseSupport';
import ExercisePrompt from '../../../student/components/exercises/ExercisePrompt';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { Box, ButtonBase, Typography } from '@mui/material';
import React from 'react';
import { ChooseNextExercise } from '../../../student/mockLessonContent';
import { BLUE, cardSx, INTER, NAVY, SLATE, SURFACE_SOFT } from '../../../student/studentStyles';
import ExerciseLabel from '../../../student/components/exercises/ExerciseLabel';
import TeacherNote from '../../../student/components/exercises/TeacherNote';
import { ExerciseReady, useExerciseLayout } from './exerciseLayout';

interface ChooseNextExerciseCardProps {
    exercise: ChooseNextExercise;
    onReady: ExerciseReady;
}

// "Select next dialog" — a branch point, usually so the student can skip parts of
// the lesson. The teacher's response for the pick is echoed. In the mockup the
// player follows the selected destination when the student presses Continue.
export default function ChooseNextExerciseCard({ exercise, onReady }: ChooseNextExerciseCardProps) {
    const desktop = useExerciseLayout() === 'desktop';
    const [pickedIndex, setPickedIndex] = React.useState<number | null>(null);

    const handlePick = (index: number) => {
        setPickedIndex(index);
        onReady('done', exercise.options[index].destination);
    };

    return (
        <Box>
            <ExerciseLabel>WHAT&apos;S NEXT</ExerciseLabel>
            <ExercisePrompt text={exercise.prompt} />
            <Box sx={{ mt: 2.5, display: 'grid', gridTemplateColumns: desktop ? 'repeat(2, minmax(0, 1fr))' : '1fr', gap: 1.25 }}>
                {exercise.options.map((option, index) => {
                    const isPicked = pickedIndex === index;
                    return (
                        <ButtonBase
                            key={option.title}
                            aria-pressed={isPicked}
                            onClick={() => handlePick(index)}
                            sx={{
                                ...cardSx,
                                width: '100%', p: 2, display: 'flex', alignItems: 'center', gap: 1.5, textAlign: 'left',
                                gridColumn: desktop && exercise.options.length % 2 === 1 && index === exercise.options.length - 1 ? '1 / -1' : undefined,
                                maxWidth: desktop && exercise.options.length % 2 === 1 && index === exercise.options.length - 1 ? 'calc(50% - 6px)' : undefined,
                                justifySelf: 'center',
                                border: `2px solid ${isPicked ? BLUE : 'transparent'}`,
                                backgroundColor: isPicked ? `${BLUE}0A` : '#fff',
                                transition: 'background-color 150ms ease, border-color 150ms ease',
                            }}
                        >
                            <Box sx={{
                                width: 44, height: 44, borderRadius: '14px', flexShrink: 0,
                                display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: SURFACE_SOFT,
                            }}>
                                <Typography sx={{ fontSize: 22, lineHeight: 1 }}>{option.emoji}</Typography>
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontFamily: INTER, fontWeight: 600, fontSize: 15, color: NAVY, lineHeight: 1.3 }}>
                                    {option.title}
                                </Typography>
                                <Typography sx={{ fontFamily: INTER, fontSize: 12, color: SLATE, mt: 0.25 }}>
                                    {option.detail}
                                </Typography>
                            </Box>
                            <ChevronRightRoundedIcon sx={{ color: isPicked ? BLUE : SLATE }} />
                        </ButtonBase>
                    );
                })}
            </Box>
            {pickedIndex != null && (
                <Box sx={{ mt: 2 }}>
                    <TeacherNote tone="success" text={exercise.options[pickedIndex].response} />
                </Box>
            )}
            <ExerciseSupport exercise={exercise} />
        </Box>
    );
}
